import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Spin } from 'antd';
import {
  RocketOutlined,
  PauseCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { Line, Pie } from '@ant-design/plots';
import api from '../utils/api';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, activityRes, performanceRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-activity'),
        api.get('/dashboard/performance?days=7')
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setPerformance(performanceRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const campaignColumns = [
    {
      title: '系列名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          'ACTIVE': { color: 'success', text: '活跃' },
          'PAUSED': { color: 'warning', text: '暂停' },
          'DELETED': { color: 'error', text: '删除' },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
    },
  ];

  const taskColumns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '类型',
      dataIndex: 'task_type',
      key: 'task_type',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          'pending': { color: 'default', icon: <ClockCircleOutlined /> },
          'running': { color: 'processing', icon: <ClockCircleOutlined /> },
          'completed': { color: 'success', icon: <CheckCircleOutlined /> },
          'failed': { color: 'error', icon: <ExclamationCircleOutlined /> },
        };
        const { color, icon } = statusMap[status] || { color: 'default', icon: null };
        return <Tag color={color} icon={icon}>{status}</Tag>;
      },
    },
    {
      title: '进度',
      dataIndex: 'progress_percent',
      key: 'progress_percent',
      render: (percent) => `${percent}%`,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <h2>仪表盘</h2>
      
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日创建"
              value={stats?.campaigns?.today_created || 0}
              prefix={<RocketOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃系列"
              value={stats?.campaigns?.active || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日关停"
              value={stats?.campaigns?.today_auto_paused || 0}
              prefix={<PauseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总消耗"
              value={stats?.campaigns?.total_spend || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 最近活动 */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="最近广告系列" extra={<a href="/campaigns">查看全部</a>}>
            <Table
              dataSource={recentActivity?.recent_campaigns || []}
              columns={campaignColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="最近任务" extra={<a href="/tasks">查看全部</a>}>
            <Table
              dataSource={recentActivity?.recent_tasks || []}
              columns={taskColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* 性能指标 */}
      {performance && (
        <Card title="最近7天性能" style={{ marginTop: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title="总展示" value={performance.total_impressions} />
            </Col>
            <Col span={6}>
              <Statistic title="总点击" value={performance.total_clicks} />
            </Col>
            <Col span={6}>
              <Statistic title="平均CTR" value={performance.avg_ctr} suffix="%" />
            </Col>
            <Col span={6}>
              <Statistic title="平均ROAS" value={performance.avg_roas} />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
